import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useChatAuth } from "@/context/ChatAuthContext";
import { Loader2, MessageCircle, Zap } from "lucide-react";
import { toast } from "sonner";

export default function ChatLogin() {
  const { user, loading, login } = useChatAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!loading && user) return <Navigate to="/chat" replace />;

  const startGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/chat";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/chat");
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center px-6 py-20" data-testid="chat-login">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <span className="inline-flex items-center justify-center w-11 h-11 border border-yellow-400/40 bg-yellow-400/5">
            <Zap className="w-6 h-6 text-yellow-400" strokeWidth={2.5} />
          </span>
          <div className="font-heading">
            <div className="text-white font-black text-xl uppercase tracking-tight leading-none">Vortex Ghost</div>
            <div className="text-[10px] tracking-[0.28em] text-zinc-500 font-mono uppercase mt-1">Storm Comms Channel</div>
          </div>
        </div>

        <div className="card-vg p-8">
          <div className="overline">Live Chat</div>
          <h1 className="font-heading text-3xl font-bold mt-3 flex items-center gap-3">
            <MessageCircle className="w-6 h-6 text-yellow-400" /> Sign in to chat
          </h1>

          <button
            onClick={startGoogleAuth}
            className="mt-8 w-full py-3 px-4 bg-white text-black font-medium hover:bg-zinc-200 flex items-center justify-center gap-3"
            data-testid="chat-google-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.1C29.2 35 26.7 36 24 36c-5.4 0-9.7-3.5-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z" />
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.1C40.9 35.9 44 30.5 44 24c0-1.3-.1-2.4-.4-3.5z" />
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="chat-login-form">
            <div>
              <label className="overline mb-2 block !text-zinc-400">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="input-vg" required data-testid="chat-login-email"
              />
            </div>
            <div>
              <label className="overline mb-2 block !text-zinc-400">Password</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="input-vg" required data-testid="chat-login-password"
              />
            </div>

            {error && (
              <div className="text-red-400 text-sm border border-red-500/30 bg-red-500/5 p-3" data-testid="chat-login-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="chat-login-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-zinc-400">
            New here?{" "}
            <Link to="/chat-register" className="text-yellow-400 hover:underline" data-testid="chat-register-link">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
